import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import Breadcrumbs from "components/Common/Breadcrumb";
import { formatCurrency, calculatePercentage } from "utils/formatters";
import toast from "react-hot-toast";
import ToastNotification from "components/Toast/ToastNotification";
import check from "assets/icons/check-circle.svg";
import slash from "assets/icons/slash-circle.svg";
import { Container, Row, Col, Card, CardBody, Form, Label, Input, Button, FormFeedback, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Alert, InputGroup, InputGroupText } from "reactstrap";
import ShimmerLoading from "components/Common/Shimmer";
import { useFormik } from "formik";
import { useParams, useNavigate } from "react-router-dom";
import DatePicker from "react-tailwindcss-datepicker";
import MultiSelectWithTable from "components/MultiSelectWithTable/MultiSelectWithTable";
import Image from "components/Common/Image";
import { getImageUrl, ImageSourceType } from "utils/imageUtils";

// API
import { useGetCommissionsQuery } from "services/commissions/api";
import { useGetFilterOptionsQuery } from "services/filters/api";
import { useAddCampaignMutation, useUpdateCampaignMutation, useGetCampaignByIdQuery } from "services/campaigns/api";
import { CampaignWithProductSchema, CampaignWithProductValidationSchema } from "model/campaigns/campaign.schema";

const bonusTypes = [
  { value: "Percent", label: "โบนัส %" },
  { value: "Fixed", label: "โบนัสระบุยอดเงิน" },
];

const quotaTypes = [
  { value: "Unlimited", label: "ไม่จำกัด" },
  { value: "Limited", label: "จำกัด" },
];

const statusTypes = [
  { value: "Active", label: "พร้อมใช้งาน" },
  { value: "Inactive", label: "ไม่พร้อมใช้งาน" },
];

const CampaignsForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const title = id ? "Edit Campaign" : "Create Campaign";
  document.title = `${title} | ${process.env.REACT_APP_CMS_TITLE}`;

  // STATE
  const [bonusTypeDropdownOpen, setBonusTypeDropdownOpen] = useState(false);
  const [quotaDropdownOpen, setQuotaDropdownOpen] = useState(false);
  const [campaignData, setCampaignData] = useState(null);

  // API
  const { data, error, isLoading } = useGetCampaignByIdQuery(id, {
    skip: !id,
  });
  const [addCampaign, { isLoading: isAdding }] = useAddCampaignMutation();
  const [updateCampaign, { isLoading: isUpdating }] = useUpdateCampaignMutation();
  const { data: productsData } = useGetCommissionsQuery({
    pagination: { page: 1, limit: 1000 },
  });

  const isSaving = isAdding || isUpdating;

  const { data: filterOptionsData } = useGetFilterOptionsQuery();

  const getInitialValues = () => {
    if (id && data) {
      return data;
    }

    return {
      id: id ? parseInt(id) : undefined,
      name: "",
      condition: "",
      bonusType: "Percent",
      bonusRate: 0,
      bonusAmount: 0,
      quotaType: "Unlimited",
      quotaLimit: 0,
      status: "Active",
      isActive: true,
      startDate: "",
      endDate: "",
      products: [],
    };
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: CampaignWithProductValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        // Convert string numbers to actual numbers
        const numericValues = {
          ...values,
          bonusRate: values.bonusRate ? Number(values.bonusRate) : 0,
          bonusAmount: values.bonusAmount ? Number(values.bonusAmount) : 0,
          quotaLimit: values.quotaLimit ? Number(values.quotaLimit) : 0,
        };

        // Transform products array to only include product IDs
        const payload = {
          ...numericValues,
          products: values.products?.map((product) => product.id) || [],
        };

        if (id) {
          // Update existing campaign
          const result = await updateCampaign(payload);
          let toastId = toast.custom(<ToastNotification variant="success" icon={check} title="อัปเดตแคมเปญสำเร็จ" description={`แคมเปญ "${values.name}" ถูกอัปเดตเรียบร้อยแล้ว`} primaryButtonText="ดูรายการแคมเปญ" onPrimaryClick={() => toast.dismiss(toastId)} onSecondaryClick={() => toast.dismiss(toastId)} />, { position: "top-right", duration: 5000 });
        } else {
          // Create new campaign
          const result = await addCampaign(payload);
          let toastId = toast.custom(<ToastNotification variant="success" icon={check} title="บันทึกแคมเปญสำเร็จ" description={`แคมเปญ "${values.name}" ถูกบันทึกเรียบร้อยแล้ว`} primaryButtonText="ดูรายการแคมเปญ" onPrimaryClick={() => toast.dismiss(toastId)} onSecondaryClick={() => toast.dismiss(toastId)} />, { position: "top-right", duration: 5000 });
        }

        navigate("/campaigns");
      } catch (error) {
        const errorMessage = error.message || "ไม่สามารถบันทึกแคมเปญได้ กรุณาลองใหม่อีกครั้ง";
        let toastId = toast.custom(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description={errorMessage} onSecondaryClick={() => toast.dismiss(toastId)} />, { position: "top-right", duration: 5000 });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const toggleBonusTypeDropdown = () => setBonusTypeDropdownOpen(!bonusTypeDropdownOpen);
  const toggleQuotaDropdown = () => setQuotaDropdownOpen(!quotaDropdownOpen);

  const handleStartDateChange = (value) => {
    if (formik.values.endDate && new Date(value.startDate) > new Date(formik.values.endDate)) {
      formik.setFieldValue("endDate", value.startDate);
    }
    formik.setFieldValue("startDate", value.startDate);
  };

  const handleEndDateChange = (value) => {
    formik.setFieldValue("endDate", value.startDate);
  };

  return (
    <div className="page-content" data-testid="Campaigns-page">
      <Container fluid>
        <Breadcrumbs title={title} breadcrumbItem={title} back="กลับหน้ารายการแคมเปญ" />
        <div className="mb-3">
          <Form onSubmit={formik.handleSubmit}>
            <Card className="card-info mt-4">
              {isLoading ? (
                <div className="text-center">
                  <ShimmerLoading fullWidth={true} height={800} />
                </div>
              ) : error ? (
                <Alert color="danger">{error.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"}</Alert>
              ) : (
                <CardBody>
                  <h2 className="fw-semibold">{id ? "แก้ไขข้อมูลแคมเปญของคุณ" : "เพิ่มข้อมูลแคมเปญของคุณ"}</h2>
                  <hr className="divider-line" />

                  <div className="d-flex flex-column mt-3 gap-4">
                    <Row>
                      <Col md={2} className="d-flex align-items-start">
                        <Label className="text-xl fw-semibold my-auto">ชื่อแคมเปญ</Label>
                      </Col>
                      <Col md={10}>
                        <Input type="text" name="name" placeholder="กรอกชื่อแคมเปญ" value={formik.values.name} onChange={formik.handleChange} onBlur={formik.handleBlur} invalid={!!(formik.touched.name && formik.errors.name)} />
                        {formik.touched.name && formik.errors.name && <FormFeedback>{formik.errors.name}</FormFeedback>}
                      </Col>
                    </Row>

                    <Row>
                      <Col md={2} className="d-flex align-items-start">
                        <Label className="text-xl fw-semibold my-auto">เลือกสินค้าเข้าร่วมแคมเปญ</Label>
                      </Col>
                      <Col md={10}>
                        {productsData && (
                          <>
                            <MultiSelectWithTable
                              title="เลือกสินค้าเข้าร่วมแคมเปญ"
                              placeholder="เลือกสินค้า"
                              searching={true}
                              initData={formik.values.products}
                              filters={
                                filterOptionsData
                                  ? [
                                      {
                                        label: "ดูตามหมวดหมู่",
                                        accessor: "category.name",
                                        data: filterOptionsData.categories.map((category) => ({
                                          id: category.id,
                                          name: category.name,
                                        })),
                                      },
                                      {
                                        label: "ดูตามแบรนด์",
                                        accessor: "brand.name",
                                        data: filterOptionsData.brands.map((brand) => ({
                                          id: brand.id,
                                          name: brand.name,
                                        })),
                                      },
                                    ]
                                  : []
                              }
                              table={{
                                data: productsData?.data || [],
                                keyField: "id",
                                keyName: "name",
                                columns: [
                                  {
                                    header: "รายการ Product Affiliate",
                                    accessor: "name",
                                    sortable: true,
                                    render: (item) => (
                                      <div className="product-info d-flex align-items-center gap-3">
                                        <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
                                        <div>
                                          <div className="text-lg fw-semibold">{item.name}</div>
                                          <div className="text-md text-gray-500">SKU: {item.sku}</div>
                                        </div>
                                      </div>
                                    ),
                                  },
                                  {
                                    header: "ราคา",
                                    accessor: "price",
                                    align: "end",
                                    width: 100,
                                    render: (item) => formatCurrency(item.price),
                                  },
                                  {
                                    header: "ค่าคอมมิชชัน",
                                    accessor: "commission",
                                    align: "end",
                                    width: 100,
                                    render: (item) => {
                                      return formatCurrency(calculatePercentage(item.price, item.commission?.commissionRate));
                                    },
                                  },
                                ],
                              }}
                              onChange={(selectedItems) => {
                                formik.setFieldValue("products", selectedItems);
                              }}
                            />
                            {formik.touched.products && formik.errors.products && <FormFeedback>{formik.errors.products}</FormFeedback>}
                          </>
                        )}
                      </Col>
                    </Row>

                    <Row>
                      <Col md={2} className="d-flex align-items-start">
                        <Label className="text-xl fw-semibold my-auto">โบนัสที่พาร์ทเนอร์จะได้</Label>
                      </Col>
                      <Col md={10}>
                        <Row>
                          <Col md={6}>
                            <Dropdown isOpen={bonusTypeDropdownOpen} toggle={toggleBonusTypeDropdown}>
                              <DropdownToggle caret color="light" className="w-100 text-start">
                                {bonusTypes.find((type) => type.value === formik.values.bonusType)?.label}
                              </DropdownToggle>
                              <DropdownMenu>
                                {bonusTypes.map((type) => (
                                  <DropdownItem
                                    key={type.value}
                                    onClick={() => {
                                      formik.setFieldValue("bonusType", type.value);
                                      formik.setFieldTouched("bonusType", true);
                                    }}
                                    active={formik.values.bonusType === type.value}
                                  >
                                    {type.label}
                                  </DropdownItem>
                                ))}
                              </DropdownMenu>
                            </Dropdown>
                            {formik.touched.bonusType && formik.errors.bonusType && <FormFeedback>{formik.errors.bonusType}</FormFeedback>}
                          </Col>
                          <Col md={6}>
                            <InputGroup>
                              <Input
                                type="number"
                                name="bonusValue"
                                placeholder={formik.values.bonusType === "Percent" ? "กรอกเปอร์เซ็นต์โบนัส" : "กรอกจำนวนเงินโบนัส"}
                                value={formik.values.bonusType === "Percent" ? formik.values.bonusRate : formik.values.bonusAmount}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (formik.values.bonusType === "Percent") {
                                    formik.setFieldValue("bonusRate", value ? Number(value) : '');
                                  } else {
                                    formik.setFieldValue("bonusAmount", value ? Number(value) : '');
                                  }
                                }}
                                onBlur={formik.handleBlur}
                                invalid={!!((formik.touched.bonusRate && formik.errors.bonusRate) || (formik.touched.bonusAmount && formik.errors.bonusAmount))}
                              />

                              <InputGroupText>{formik.values.bonusType === "Percent" ? "%" : "บาท"}</InputGroupText>
                              {formik.touched.bonusRate && formik.errors.bonusRate && <FormFeedback>{formik.errors.bonusRate}</FormFeedback>}
                              {formik.touched.bonusAmount && formik.errors.bonusAmount && <FormFeedback>{formik.errors.bonusAmount}</FormFeedback>}
                            </InputGroup>
                          </Col>
                        </Row>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={2} className="d-flex align-items-start">
                        <Label className="text-xl fw-semibold my-auto">จำนวนโควต้าของแคมเปญ</Label>
                      </Col>
                      <Col md={10}>
                        <Row>
                          <Col md={5}>
                            <Dropdown isOpen={quotaDropdownOpen} toggle={toggleQuotaDropdown}>
                              <DropdownToggle caret color="light" className="w-100 text-start">
                                {quotaTypes.find((type) => type.value === formik.values.quotaType)?.label}
                              </DropdownToggle>
                              <DropdownMenu>
                                {quotaTypes.map((type) => (
                                  <DropdownItem
                                    key={type.value}
                                    onClick={() => {
                                      formik.setFieldValue("quotaType", type.value);
                                      formik.setFieldTouched("quotaType", true);
                                    }}
                                    active={formik.values.quotaType === type.value}
                                  >
                                    {type.label}
                                  </DropdownItem>
                                ))}
                              </DropdownMenu>
                            </Dropdown>
                            {formik.touched.quotaType && formik.errors.quotaType && <FormFeedback>{formik.errors.quotaType}</FormFeedback>}
                          </Col>
                          {formik.values.quotaType === "Limited" && (
                            <Col md={7}>
                              <Input
                                type="number"
                                name="quotaLimit"
                                placeholder="กรอกจำนวนโควต้า"
                                value={formik.values.quotaLimit}
                                onChange={(e) => {
                                  formik.setFieldValue("quotaLimit", e.target.value);
                                }}
                                onBlur={formik.handleBlur}
                                invalid={!!(formik.touched.quotaLimit && formik.errors.quotaLimit)}
                                disabled={formik.values.quotaType !== "Limited"}
                              />
                              {formik.touched.quotaLimit && formik.errors.quotaLimit && <FormFeedback>{formik.errors.quotaLimit}</FormFeedback>}
                            </Col>
                          )}
                        </Row>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Row>
                          <Col md={4} className="d-flex align-items-start">
                            <Label className="text-xl fw-semibold my-auto">วันที่เริ่มแคมเปญ</Label>
                          </Col>
                          <Col md={8}>
                            <div className="datepicker">
                              <DatePicker
                                useRange={false}
                                asSingle={true}
                                value={{
                                  startDate: formik.values.startDate,
                                  endDate: formik.values.startDate,
                                }}
                                onChange={handleStartDateChange}
                                displayFormat="DD/MM/YYYY"
                                inputClassName={`form-control ${formik.touched.startDate && formik.errors.startDate ? "is-invalid" : ""}`}
                              />
                              {formik.touched.startDate && formik.errors.startDate && <FormFeedback>{formik.errors.startDate}</FormFeedback>}
                            </div>
                          </Col>
                        </Row>
                      </Col>
                      <Col md={6}>
                        <Row>
                          <Col md={4} className="d-flex align-items-start">
                            <Label className="text-xl fw-semibold my-auto">วันที่สิ้นสุดแคมเปญ</Label>
                          </Col>
                          <Col md={8}>
                            <div className="datepicker">
                              <DatePicker
                                useRange={false}
                                asSingle={true}
                                value={{
                                  startDate: formik.values.endDate,
                                  endDate: formik.values.endDate,
                                }}
                                onChange={handleEndDateChange}
                                displayFormat="DD/MM/YYYY"
                                inputClassName={`form-control ${formik.touched.endDate && formik.errors.endDate ? "is-invalid" : ""}`}
                              />
                            </div>
                            {formik.touched.endDate && formik.errors.endDate && <FormFeedback>{formik.errors.endDate}</FormFeedback>}
                          </Col>
                        </Row>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={2} className="d-flex align-items-start">
                        <Label className="text-xl fw-semibold my-auto">สถานะหลังสร้าง</Label>
                      </Col>
                      <Col md={10}>
                        <div className="d-flex gap-4">
                          {statusTypes.map((type) => (
                            <div key={type.value} className="d-flex gap-2 align-items-center">
                              <Input
                                type="radio"
                                id={`status-${type.value}`}
                                name="status"
                                value={type.value}
                                defaultChecked={formik.values.status === type.value}
                                onChange={() => {
                                  formik.setFieldValue("status", type.value);
                                  formik.setFieldTouched("status", true);
                                }}
                                onBlur={formik.handleBlur}
                                invalid={!!(formik.touched.status && formik.errors.status)}
                                className="mt-2"
                              />
                              <Label check className="text-xl" htmlFor={`status${type.value}`}>
                                {type.label}
                              </Label>
                            </div>
                          ))}
                          {formik.touched.status && formik.errors.status && <FormFeedback>{formik.errors.status}</FormFeedback>}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={2} className="d-flex align-items-start">
                        <Label className="text-xl fw-semibold">เงื่อนไขของแคมเปญที่ให้พาร์ทเนอร์อ่าน</Label>
                      </Col>
                      <Col md={10}>
                        <Input type="textarea" name="condition" placeholder="กรอกเงื่อนไขของแคมเปญ" value={formik.values.condition} onChange={formik.handleChange} onBlur={formik.handleBlur} invalid={!!(formik.touched.condition && formik.errors.condition)} rows={5} />
                        {formik.touched.condition && formik.errors.condition && <FormFeedback>{formik.errors.condition}</FormFeedback>}
                      </Col>
                    </Row>
                  </div>
                </CardBody>
              )}
            </Card>
            <div className="text-end mt-4">
              <div className="d-flex justify-content-between gap-3 w-100">
                <Button
                  color="light"
                  outline
                  className="w-52"
                  onClick={() => {
                    navigate("/campaigns");
                  }}
                >
                  ยกเลิก
                </Button>
                <Button className="w-52" color="primary" type="submit" disabled={formik.isSubmitting || isSaving}>
                  {formik.isSubmitting || isSaving ? "กำลังบันทึก..." : "บันทึก"}
                </Button>
              </div>
            </div>
          </Form>
        </div>
      </Container>
    </div>
  );
};

export default withTranslation()(CampaignsForm);
